import React, { useRef, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatView } from "@/features/ai-chat/components/ChatView";
import { InitialView } from "@/features/ai-chat/components/InitialView";
import { ChatInput } from "@/features/ai-chat/components/ChatInput";
import { useChat } from "@/features/ai-chat/hooks/useChat";
import { useFileHandler } from "@/features/ai-chat/hooks/useFileHandler";
import "./home.css";

const AILearningHomepage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const chatViewRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const previousUrlConversationId = useRef(null);
  const justCreatedConversationId = useRef(null);
  const [message, setMessage] = useState("");

  const {
    chatHistory,
    conversationId: currentConversationId,
    isLoading,
    error,
    sendChatMessage,
    loadConversation,
    clearChat,
  } = useChat();

  const { files, handleFileChange, handlePaste, removeFile, clearFiles } =
    useFileHandler();

  // Load conversation only when URL param actually changes
  useEffect(() => {
    const urlChanged = conversationId !== previousUrlConversationId.current;
    previousUrlConversationId.current = conversationId;

    if (!urlChanged) return;

    // Skip if this is the conversation we just created
    if (conversationId === justCreatedConversationId.current) {
      justCreatedConversationId.current = null;
      return;
    }

    if (conversationId && conversationId !== currentConversationId) {
      loadConversation(conversationId);
    } else if (!conversationId && chatHistory.length > 0) {
      clearChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, loadConversation, clearChat]);

  const handleSendMessage = useCallback(
    async (msgContent = message) => {
      if (!msgContent.trim() && files.length === 0) return;

      const result = await sendChatMessage(msgContent);
      setMessage("");
      clearFiles();

      // Navigate to new conversation URL
      if (result?.isNewConversation && !conversationId) {
        justCreatedConversationId.current = result.conversationId;
        navigate(`/student/home/${result.conversationId}`, { replace: true });
      }

      // Scroll to latest message
      setTimeout(() => {
        chatViewRef.current?.scrollLastMessageToTop?.();
      }, 100);
    },
    [message, files, sendChatMessage, clearFiles, conversationId, navigate]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const hasMessages = chatHistory.length > 0;

  return (
    <section
      className={`relative h-full ${
        hasMessages ? "dark:bg-[#212121]" : "home-ai-gradient-bg"
      }`}
    >
      {error && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg z-30">
          {error}
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="h-full overflow-y-auto pb-48 hide-scrollbar"
      >
        {!hasMessages && (
          <div className="absolute inset-0 pointer-events-none home-ai-background-overlay" />
        )}

        {hasMessages ? (
          <ChatView
            ref={chatViewRef}
            chatHistory={chatHistory}
            isLoading={isLoading}
            scrollContainerRef={scrollContainerRef}
          />
        ) : (
          <InitialView handleActionClick={handleSendMessage} />
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-20">
        <div className="pointer-events-auto">
          <ChatInput
            message={message}
            setMessage={setMessage}
            files={files}
            handleFileChange={handleFileChange}
            handlePaste={handlePaste}
            removeFile={removeFile}
            handleSendMessage={handleSendMessage}
            handleKeyDown={handleKeyDown}
            isLoading={isLoading}
          />
        </div>
      </div>
    </section>
  );
};

export default AILearningHomepage;
